import { useCallback, useState } from "react";
import { SharedFormField } from "../shared";
import ChoiceGroup from "../forms/ChoiceGroup";
import CheckboxGroup from "../forms/CheckboxGroup";
import { useSellerState } from "../../js/state/sellerState";
import { useAutosave } from "../../js/hooks/useAutosave";
import { useGuestMode } from "../../js/hooks/useGuestMode";

export default function SellerReadinessPanel() {
  const { seller, updatePortfolio } = useSellerState();
  const { canPersist } = useGuestMode();
  const saved = seller.portfolio.listingReadiness || {};
  const [form, setForm] = useState({
    staging: saved.staging || "",
    clutter: saved.clutter || "",
    documents: saved.documents || [],
    access: saved.access || "",
    occupancyAtList: saved.occupancyAtList || "",
    notes: saved.notes || "",
  });

  const persist = useCallback(
    (next) => {
      if (!canPersist) return;
      updatePortfolio("listingReadiness", next);
    },
    [canPersist, updatePortfolio]
  );

  useAutosave(canPersist ? form : null, persist);

  function update(event) {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  }

  return (
    <div className="sidebar-panel">
      <h3 className="sidebar-panel__title">Listing readiness</h3>
      <p className="muted sidebar-panel__lead">Staging, documents, and access. This stays in the sidebar.</p>
      <ChoiceGroup
        label="Staging plan"
        name="staging"
        value={form.staging}
        onChange={update}
        options={[
          { value: "as-lived", label: "Show as lived-in" },
          { value: "declutter", label: "Declutter / edit" },
          { value: "pro", label: "Professional staging" },
          { value: "vacant", label: "Vacant / ready" },
        ]}
      />
      <ChoiceGroup
        label="Clutter / storage"
        name="clutter"
        value={form.clutter}
        onChange={update}
        options={[
          { value: "ready", label: "Already camera-ready" },
          { value: "weekend", label: "A weekend of work" },
          { value: "storage", label: "Need a storage unit" },
        ]}
      />
      <CheckboxGroup
        label="Documents you can provide"
        name="documents"
        values={form.documents}
        onChange={update}
        layout="grid"
        options={[
          { value: "survey", label: "Survey" },
          { value: "hoa", label: "HOA docs" },
          { value: "taxes", label: "Tax record" },
          { value: "utilities", label: "Utility averages" },
          { value: "warranties", label: "Warranties" },
          { value: "permits", label: "Permits / receipts" },
          { value: "disclosure", label: "Seller disclosure draft" },
        ]}
      />
      <ChoiceGroup
        label="Showing access"
        name="access"
        value={form.access}
        onChange={update}
        options={[
          { value: "lockbox", label: "Lockbox OK" },
          { value: "appointment", label: "Appointment only" },
          { value: "tenant", label: "Coordinate with tenant" },
          { value: "limited", label: "Limited hours" },
        ]}
      />
      <ChoiceGroup
        label="Occupancy at list"
        name="occupancyAtList"
        value={form.occupancyAtList}
        onChange={update}
        options={[
          { value: "in-place", label: "We will still be in the home" },
          { value: "overlap", label: "Short overlap" },
          { value: "vacant", label: "Vacant at list" },
        ]}
      />
      <SharedFormField
        label="Readiness notes for my agent"
        name="notes"
        type="textarea"
        value={form.notes}
        onChange={update}
        placeholder="Pets, alarm process, preferred photographer day."
      />
    </div>
  );
}
